import { Component, inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BvsHaftpflichtUndKaskoFormModel } from '../../models/form/bvs-haftpflicht-und-kasko-form.model';
import { CalldiusStepItem, CallidusStepperComponent } from '@shared/components/callidus-stepper/callidus-stepper.component';
import { BoatDataHaftpflichtKaskoComponent } from '../../components/boat-data-haftpflicht-kasko/boat-data-haftpflicht-kasko.component';
import { createBvsHaftpflichtKaskoForm } from '../../factories/bvs-kasko-form.factory';
import {
  AdditionalCoverageHaftpflichtKaskoDataComponent
} from '../../components/additional-coverage-haftpflicht-kasko-data/additional-coverage-haftpflicht-kasko-data.component';
import { CustomerDataComponent } from '@shared/components/customer-data/customer-data.component';
import { DataSubmittedComponent } from '@shared/components/data-submitted/data-submitted.component';
import { PricingDataComponent } from '@shared/components/pricing-data/pricing-data.component';
import { takeUntil } from 'rxjs';
import { BvsBaseComponent } from '@shared/directives/bvs-base.component';
import { Router } from '@angular/router';
import {
  BrokernetLanguageType,
  CallidusBvsHaftpflichtBoatInformation,
  CallidusBvsHaftpflichtFigures,
  CallidusBvsHaftpflichtUndKaskoBoatInformation,
  CallidusBvsHaftpflichtUndKaskoFigures,
  PublicAddressCreateDto,
  PublicCallidusBvsHaftpflichtUndKaskoContractCreateDto,
  PublicPersonCreateDto
} from '@backend/models';
import { BvsHaftpflichtStateService } from '@shared/services/bvs-haftpflicht-state.service';
import { UrlParamProviderService } from '@shared/services/url-param-provider.service';

@Component({
  selector: 'app-bvs-haftpflicht-kasko-page',
  imports: [
    BoatDataHaftpflichtKaskoComponent,
    CustomerDataComponent,
    CallidusStepperComponent,
    DataSubmittedComponent,
    PricingDataComponent,
    AdditionalCoverageHaftpflichtKaskoDataComponent
  ],
  providers: [BvsHaftpflichtStateService],
  templateUrl: './bvs-haftpflicht-kasko-page.component.html',
  styleUrl: './bvs-haftpflicht-kasko-page.component.scss'
})
export class BvsHaftpflichtKaskoPageComponent extends BvsBaseComponent implements OnInit {
  private readonly bvsHaftpflichtStateService = inject(BvsHaftpflichtStateService);
  private readonly router = inject(Router);
  private readonly urlParamProviderService = inject(UrlParamProviderService);

  curentNavigationStep = HaftpflichtKaskoNavigationEnum.Base;
  form!: FormGroup<BvsHaftpflichtUndKaskoFormModel>;

  stepItems: CalldiusStepItem[] = [
    { id: '1', label: 'bvs.calculator.menu.boat', iconUrl: '/assets/icons/boat.svg' },
    { id: '2', label: 'bvs.calculator.menu.finance', iconUrl: '/assets/icons/euro-symbol.svg' },
    { id: '3', label: 'bvs.calculator.menu.finish', iconUrl: '/assets/icons/health-shield.svg' }
  ];

  protected readonly FormGroup = FormGroup;
  protected readonly HaftpflichtKaskoNavigationEnum = HaftpflichtKaskoNavigationEnum;

  private stepOrder: HaftpflichtKaskoNavigationEnum[] = [
    HaftpflichtKaskoNavigationEnum.Base,
    HaftpflichtKaskoNavigationEnum.Pricing,
    HaftpflichtKaskoNavigationEnum.AdditionalCoverage,
    HaftpflichtKaskoNavigationEnum.Customer,
    HaftpflichtKaskoNavigationEnum.Done
  ];

  ngOnInit(): void {
    this.form = createBvsHaftpflichtKaskoForm();

    this.form?.controls.bvsBoatInformation.controls.boatType.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(boatType => {
      if (boatType !== null) {
        this.setValidatorsBasedOnBoatType(this.form, boatType);
      }
    });
  }

  getCurrentPosition(): number {
    return this.stepOrder.indexOf(this.curentNavigationStep);
  }

  moveToNextStep(): void {
    this.curentNavigationStep++;
  }

  moveToPreviousStep(): void {
    if (this.curentNavigationStep === HaftpflichtKaskoNavigationEnum.Base) {
      void this.router.navigate(['/']);
    }

    this.curentNavigationStep--;
  }

  submitData(): void {
    const person = this.form?.controls.customerData.controls.person.getRawValue();
    const legalChecks = this.form.controls.customerData.controls.legalChecks.controls;
    const figures = this.form.controls.bvsFigures.controls;
    const boatInformation = this.form.controls.bvsBoatInformation.controls;

    const query = this.urlParamProviderService.getQueryData();
    const isLinked = !!query?.customerId && !!query?.personId;

    const onboardingDto: PublicCallidusBvsHaftpflichtUndKaskoContractCreateDto = {
      publicLegalCheck: {
        hatElektronischeKommunikationAkzeptiert: legalChecks.isContactConsent.value ?? false,
        hatDsgvoAkzeptiert: legalChecks.isGdprAccepted.value ?? false,
        hatAvbAkzeptiert: false,
        hatIpidAkzeptiert: false,
        hatSepaEinzugAkzeptiert: false,
        hatWiderrufsrechtAkzeptiert: false,
        hatWuenscheUndBeduerfnisseAkzeptiert: false
      },

      ...(isLinked
        ? { publicLinkCustomerDto: { customerId: query!.customerId!, personId: query!.personId! } }
        : {
          publicPerson: {
            titlePrefix: person.titlePrefix,
            titleSuffix: person.titleSuffix,
            firstname: person.firstname,
            lastname: person.lastname,
            birthdate: person.birthdate,
            gender: person.gender,
            brokernetLanguageType: localStorage.getItem('app-language') === BrokernetLanguageType.En ? BrokernetLanguageType.En : BrokernetLanguageType.De,
            publicContactInfoCreateDto: {
              email: person.email,
              phone: person.phone
            },
            publicAddressCreateDto: this.form.controls.customerData.controls.address.getRawValue() as PublicAddressCreateDto
          } as PublicPersonCreateDto
        }),

      callidusBvsHaftpflichtUndKaskoFigures: {
        callidusBvsHaftpflichtFigures: {
          isRechtsschutzSelected: figures.isRechtsschutzSelected.value ?? false,
          isKeinSelbstbehaltBeiRechtsschutzSelected: figures.isKeinSelbstbehaltBeiRechtsschutzSelected.value ?? false,
          isCharterSelected: figures.isCharterSelected.value ?? false,
          callidusBvsVersicherungssummeKunde: figures.callidusBvsVersicherungssummeKunde.value
        } as CallidusBvsHaftpflichtFigures,

        selbstbehaltKunde: figures.selbstbehaltKunde.value,
        isMaschinendeckungSelected: figures.isMaschinendeckung.value ?? false,
        wertDesBootesInEur: figures.insuranceSumBoat.value,
        wertDerZusaetzlichenAusruestungInEur: figures.insuranceSumAdditionalEquipment.value,
        wertDerPersoenlichenEffekteInEur: figures.insuranceSumPersoenlicheEffekte.value,
        wertDesBeibootsInEur: figures.insuranceSumDinghy.value,
        wertDesTrailersInEur: figures.insuranceSumTrailer.value,
        gesamtVersicherungssummeInEur: figures.totalInsuranceSum.value,
        isAlleWerteMitMwstSelected: figures.allPricesWithVAT.value ?? false
      } as CallidusBvsHaftpflichtUndKaskoFigures,

      callidusBvsHaftpflichtUndKaskoBoatInformation: {
        callidusBvsHaftpflichtBoatInformation: {
          boatType: boatInformation.boatType.value,
          boatConstructionType: boatInformation.boatConstructionType.value ?? undefined,
          manufacturer: boatInformation.manufacturer.value,
          yachtType: boatInformation.yachtType.value,
          boatModel: boatInformation.boatModel.value,
          boatYard: boatInformation.boatYard.value,
          fullWidth: boatInformation.fullWidth.value,
          fullLength: boatInformation.fullLength.value,
          isEnginePresent: boatInformation.isEnginePresent.value ?? false,
          engineManufacturer: boatInformation.engineManufacturer.value,
          engineModel: boatInformation.engineModel.value,
          engineFuelType: boatInformation.engineFuelType.value,
          engineHp: boatInformation.engineHp.value,
          builtInEngineCount: boatInformation.builtInEngineCount.value,
          builtInEngineHp: boatInformation.builtInEngineHp.value,
          extraEngineCount: boatInformation.extraEngineCount.value,
          extraEngineHp: boatInformation.extraEngineHp.value,
          mooringSummer: boatInformation.mooringSummer.value,
          mooringWinter: boatInformation.mooringWinter.value,
          isGutachtenPresent: boatInformation.isGutachtenPresent.value ?? false,
          mastMaterial: boatInformation.mastMaterial.value ?? undefined,
          sailingArea: boatInformation.sailingArea.value,
          countryRegistered: boatInformation.countryRegistered.value,
          isComfortAusstattungVorhanden: boatInformation.isComfortAusstattungVorhanden.value ?? false,
          isSailPresent: boatInformation.isSailPresent.value ?? false,
          sailSurfaceInMetersSquared: boatInformation.sailSurfaceInMetersSquared.value,
          maxKnotsOver40: boatInformation.maxKnotsOver40.value,
          isBeibootVorhanden: boatInformation.isBeibootVorhanden.value ?? false,
          beibootHp: boatInformation.beibootHp.value
        } as CallidusBvsHaftpflichtBoatInformation,
        constructionYear: boatInformation.constructionYear.value,
        isRegattateilnahmeSelected: boatInformation.isSport.value ?? false,
        hullMaterial: boatInformation.hullMaterial.value,
        isModifikationAmBootSelected: boatInformation.isBoatModified.value ?? false,
        boatModifiedValue: boatInformation.boatModifiedValue.value
      } as CallidusBvsHaftpflichtUndKaskoBoatInformation,

      anmerkungCustomer: this.form.controls.customerData.controls.anmerkungCustomer.value!
    } as PublicCallidusBvsHaftpflichtUndKaskoContractCreateDto;

    this.bvsHaftpflichtStateService
      .submitHaftpflichtUndKaskoOnboarding(onboardingDto)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {
          this.curentNavigationStep = HaftpflichtKaskoNavigationEnum.Done;
        },
        error: (error) => {
          console.error('Error submitting onboarding:', error);
        }
      });
  }
}

export enum HaftpflichtKaskoNavigationEnum {
  Base,
  Pricing,
  AdditionalCoverage,
  Customer,
  Done
}

